import React, { useEffect, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import CloseIcon from '@mui/icons-material/Close'
import SendIcon from '@mui/icons-material/Send'
import TranslateIcon from '@mui/icons-material/Translate'
import {
  Box,
  Drawer,
  FormControlLabel,
  IconButton,
  Link,
  List,
  ListItem,
  ListItemText,
  Paper,
  Switch,
  TextField,
  Typography,
} from '@mui/material'

import { apiZodios } from '~api/apiZodios'
import { Avatar, LocationEntryInline } from '~components/common'
import { ILocationEntryInlineAddress } from '~components/common/LocationEntryInline'
import { IS_DEMO, IS_PROD } from '~config'
import { lightTheme } from '~createTheme'
import { RootState } from '~redux/rootReducer'
import { GeoPoint, IChatMessageDisplay, OrganizationAISettings0, ResponseTypeEnum } from '~types'

import ChatBubbleProgressIndicator, { ChatBubbleLoadingState } from './ChatBubbleProgressIndicator'
import { MessageBubble } from './MessageBubble'

interface ChatViewProps {
  orgSettings: OrganizationAISettings0
  testing: boolean
}

type LanguageEntry = {
  english: string
  native: string
  code: string
  onClick?: () => void
}

interface ISubmitUserMessageParams {
  text: string
  location?: GeoPoint
}

const SUPPORTED_LANGUAGES: LanguageEntry[] = [
  { english: 'Afrikaans', native: 'Afrikaans', code: 'af' },
  { english: 'Albanian', native: 'Shqip', code: 'sq' },
  { english: 'Amharic', native: 'አማርኛ', code: 'am' },
  { english: 'Arabic', native: 'العربية', code: 'ar' },
  { english: 'Armenian', native: 'Հայերեն', code: 'hy' },
  { english: 'Azerbaijani', native: 'Azərbaycan', code: 'az' },
  { english: 'Bangla', native: 'বাংলা', code: 'bn' },
  { english: 'Bosnian', native: 'Bosanski', code: 'bs' },
  { english: 'Bulgarian', native: 'Български', code: 'bg' },
  { english: 'Catalan', native: 'Català', code: 'ca' },
  { english: 'Chinese (Simplified)', native: '简体中文', code: 'zh' },
  { english: 'Chinese (Traditional)', native: '繁體中文', code: 'zh-TW' },
  { english: 'Croatian', native: 'Hrvatski', code: 'hr' },
  { english: 'Czech', native: 'Čeština', code: 'cs' },
  { english: 'Danish', native: 'Dansk', code: 'da' },
  { english: 'Dari', native: 'دری', code: 'fa-AF' },
  { english: 'Dutch', native: 'Nederlands', code: 'nl' },
  { english: 'English', native: 'English', code: 'en' },
  { english: 'Estonian', native: 'Eesti', code: 'et' },
  { english: 'Farsi (Persian)', native: 'فارسی', code: 'fa' },
  { english: 'Filipino', native: 'Tagalog', code: 'tl' },
  { english: 'Finnish', native: 'Suomi', code: 'fi' },
  { english: 'French', native: 'Français', code: 'fr' },
  { english: 'French (Canada)', native: 'Français (Canada)', code: 'fr-CA' },
  { english: 'Georgian', native: 'ქართული', code: 'ka' },
  { english: 'German', native: 'Deutsch', code: 'de' },
  { english: 'Greek', native: 'Ελληνικά', code: 'el' },
  { english: 'Gujarati', native: 'ગુજરાતી', code: 'gu' },
  { english: 'Haitian Creole', native: 'Kreyòl Ayisyen', code: 'ht' },
  { english: 'Hausa', native: 'Hausa', code: 'ha' },
  { english: 'Hebrew', native: 'עברית', code: 'he' },
  { english: 'Hindi', native: 'हिन्दी', code: 'hi' },
  { english: 'Hungarian', native: 'Magyar', code: 'hu' },
  { english: 'Icelandic', native: 'Íslenska', code: 'is' },
  { english: 'Indonesian', native: 'Bahasa Indonesia', code: 'id' },
  { english: 'Irish', native: 'Gaeilge', code: 'ga' },
  { english: 'Italian', native: 'Italiano', code: 'it' },
  { english: 'Japanese', native: '日本語', code: 'ja' },
  { english: 'Kannada', native: 'ಕನ್ನಡ', code: 'kn' },
  { english: 'Kazakh', native: 'Қазақ', code: 'kk' },
  { english: 'Korean', native: '한국어', code: 'ko' },
  { english: 'Latvian', native: 'Latviešu', code: 'lv' },
  { english: 'Lithuanian', native: 'Lietuvių', code: 'lt' },
  { english: 'Macedonian', native: 'Македонски', code: 'mk' },
  { english: 'Malay', native: 'Bahasa Melayu', code: 'ms' },
  { english: 'Malayalam', native: 'മലയാളം', code: 'ml' },
  { english: 'Maltese', native: 'Malti', code: 'mt' },
  { english: 'Marathi', native: 'मराठी', code: 'mr' },
  { english: 'Mongolian', native: 'Монгол', code: 'mn' },
  { english: 'Norwegian (Bokmål)', native: 'Norsk Bokmål', code: 'no' },
  { english: 'Pashto', native: 'پښتو', code: 'ps' },
  { english: 'Polish', native: 'Polski', code: 'pl' },
  { english: 'Portuguese (Brazil)', native: 'Português (Brasil)', code: 'pt' },
  { english: 'Portuguese (Portugal)', native: 'Português (Portugal)', code: 'pt-PT' },
  { english: 'Punjabi', native: 'ਪੰਜਾਬੀ', code: 'pa' },
  { english: 'Romanian', native: 'Română', code: 'ro' },
  { english: 'Russian', native: 'Русский', code: 'ru' },
  { english: 'Serbian', native: 'Српски', code: 'sr' },
  { english: 'Sinhala', native: 'සිංහල', code: 'si' },
  { english: 'Slovak', native: 'Slovenčina', code: 'sk' },
  { english: 'Slovenian', native: 'Slovenščina', code: 'sl' },
  { english: 'Somali', native: 'Soomaali', code: 'so' },
  { english: 'Spanish', native: 'Español', code: 'es' },
  { english: 'Spanish (Mexico)', native: 'Español (México)', code: 'es-MX' },
  { english: 'Swahili', native: 'Kiswahili', code: 'sw' },
  { english: 'Swedish', native: 'Svenska', code: 'sv' },
  { english: 'Tamil', native: 'தமிழ்', code: 'ta' },
  { english: 'Telugu', native: 'తెలుగు', code: 'te' },
  { english: 'Thai', native: 'ไทย', code: 'th' },
  { english: 'Turkish', native: 'Türkçe', code: 'tr' },
  { english: 'Ukrainian', native: 'Українська', code: 'uk' },
  { english: 'Urdu', native: 'اردو', code: 'ur' },
  { english: 'Uzbek', native: "O'zbek", code: 'uz' },
  { english: 'Vietnamese', native: 'Tiếng Việt', code: 'vi' },
  { english: 'Welsh', native: 'Cymraeg', code: 'cy' },
].sort((a, b) => a.english.localeCompare(b.english))

const LanguagePrompt: React.FC<{ onShowMore: () => void }> = ({ onShowMore }) => (
  <Paper
    sx={{
      p: 2,
      mb: 2,
      maxWidth: '100%',
      bgcolor: 'white',
      borderRadius: 2,
    }}
  >
    <Box sx={{ display: 'flex', gap: 1 }}>
      <TranslateIcon color='action' />
      <Box>
        <Typography variant='body2' sx={{ fontSize: '16px' }}>
          Ask in your language of choice and we'll translate your response. Languages supported
          include: English, Español, 简体中文, Tiếng Việt, Tagalog, العربية.{' '}
          <Link
            component='button'
            variant='body2'
            onClick={onShowMore}
            sx={{
              ...lightTheme.typography.link,
              fontSize: '18px',
              textDecoration: 'none',
            }}
          >
            Show all supported languages
          </Link>
        </Typography>

        <Typography
          variant='body1'
          sx={{
            color: 'secondary.dark',
            mt: 2,
          }}
        >
          Disclaimer: AI can make mistakes. Please confirm important information.
        </Typography>
      </Box>
    </Box>
  </Paper>
)

const ChatView: React.FC<ChatViewProps> = ({ orgSettings, testing }) => {
  const isSuper = useSelector((state: RootState) => state?.auth?.user?.super)
  const [messages, setMessages] = useState<IChatMessageDisplay[]>(() => {
    const initialMessage: IChatMessageDisplay = {
      id: null,
      text: orgSettings.chat_greeting || 'Hi, how can I help?',
      sender: 'AI',
      timestamp: new Date(),
      impression: null,
    }
    return [initialMessage]
  })
  const [inputValue, setInputValue] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [isResponseComplete, setIsResponseComplete] = useState(false)
  const [isAddressWidgetOpen, setIsAddressWidgetOpen] = useState(false)
  const [userId, setUserId] = useState<string | undefined>(undefined)
  const [chatId, setChatId] = useState<string | undefined>(undefined)
  const messagesEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const latestMessageRef = useRef<HTMLDivElement>(null)
  const [languageDrawerOpen, setLanguageDrawerOpen] = useState(false)
  const [selectedLanguage, setSelectedLanguage] = useState<string>('en')
  const [loadingState, setLoadingState] = useState<ChatBubbleLoadingState>('analyzing')
  const [initialLocation, setInitialLocation] = useState<GeoPoint | null>(null)

  const [debugVisible, setDebugVisible] = useState<boolean>(true)
  const showDebugInfo = testing || (isSuper && debugVisible) || (!isSuper && !IS_DEMO && !IS_PROD)

  // HACK - Don't link back to Polimorphic.com for Morris County Surrogate
  const organizationId = orgSettings.organization.id
  const morrisCountySurrogateOrganizationIdProd = 'ffa09fe1-aaea-4984-9e13-9fa483857e2b'
  const includePolimorphicLink = organizationId !== morrisCountySurrogateOrganizationIdProd
  const DEFAULT_LOCATION = { lat: 40.712776, lon: -74.005974 } // Default to New York City

  const scrollToBottom = (): void => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
  }

  useEffect(() => {
    fetchOrganizationLocation()
  }, [orgSettings])

  useEffect(() => {
    scrollToBottom()

    if (
      !isLoading &&
      messages.length > 0 &&
      messages[messages.length - 1].sender === 'AI' &&
      latestMessageRef.current
    ) {
      setTimeout(() => {
        if (latestMessageRef.current) {
          latestMessageRef.current.focus()
        }
      }, 50)
    }
  }, [messages, isLoading])

  const fetchOrganizationLocation = async (): Promise<void> => {
    try {
      const response = await apiZodios.contacts_getOrganizationLocation({
        organization_identity_id: orgSettings.organization.identity,
      })
      setInitialLocation(response.point || null)
    } catch (error) {
      console.error('Error fetching organization location:', error)
      setInitialLocation(null)
    }
  }

  const submitMessage = async ({ text, location }: ISubmitUserMessageParams): Promise<void> => {
    if (!text.trim() || isLoading) return

    const userMessage: IChatMessageDisplay = {
      id: null,
      text: text,
      sender: 'USER',
      timestamp: new Date(),
      impression: null,
    }

    setMessages((prev) => [...prev, userMessage])
    setInputValue('')
    setIsAddressWidgetOpen(false)
    setIsLoading(true)
    setLoadingState('analyzing')

    const retrievalTimer = setTimeout(() => setLoadingState('retrieving'), 2500)
    const generatingTimer = setTimeout(() => setLoadingState('generating'), 5000)
    const waitingTimer = setTimeout(() => setLoadingState('waiting'), 10000)

    try {
      const result = await apiZodios.ai_generateResponse({
        user_id: userId || undefined,
        chat_id: chatId || undefined,
        organization_id: orgSettings.organization.id,
        interaction_type: 'CHAT',
        query: text,
        location: location,
      })

      setIsResponseComplete(true)
      clearTimeout(retrievalTimer)
      clearTimeout(generatingTimer)
      clearTimeout(waitingTimer)

      const aiMessage: IChatMessageDisplay = {
        id: result.response_id,
        text: result.response_text,
        sender: 'AI',
        sources: result.sources,
        responseType: result.response_type || ResponseTypeEnum.Enum.ANSWER,
        timestamp: new Date(),
        impression: null,
      }

      if (result.response_type === ResponseTypeEnum.Enum.REQUEST_LOCATION) {
        setIsAddressWidgetOpen(true)
      } else {
        setIsAddressWidgetOpen(false)
      }

      setTimeout(() => {
        userMessage.queryAnalysis = result.query_analysis
        setMessages((prev) => [...prev.slice(0, -1), userMessage])
        setMessages((prev) => [...prev, aiMessage])
        setChatId(chatId || result.chat_id)
        setUserId(result.user_id || '')
        setIsLoading(false)
        setIsResponseComplete(false)
      }, 500)
    } catch (error) {
      console.error('Error generating AI response:', error)
      const errorMessage: IChatMessageDisplay = {
        id: null,
        text: 'I apologize, but I encountered an error processing your request. Please try again.',
        sender: 'AI',
        timestamp: new Date(),
        responseType: ResponseTypeEnum.Enum.ANSWER,
        impression: null,
      }
      setMessages((prev) => [...prev, errorMessage])
      clearTimeout(retrievalTimer)
      clearTimeout(generatingTimer)
      clearTimeout(waitingTimer)
      setIsLoading(false)
      setIsAddressWidgetOpen(false)
    }
  }

  // Text submission handler
  const handleSubmit = async (e?: React.FormEvent): Promise<void> => {
    e?.preventDefault()
    await submitMessage({ text: inputValue })
  }

  // Address submission handler
  const handleAddAddress = async (address: ILocationEntryInlineAddress): Promise<void> => {
    await submitMessage({
      text: address.address,
      location: address.location,
    })
  }

  const handleCancelAddAddress = (): void => {
    setIsAddressWidgetOpen(false)
    setInputValue('')
  }

  const translateGreeting = async (languageCode: string): Promise<void> => {
    try {
      const result = await apiZodios.ai_translateText({
        text: orgSettings.chat_greeting || 'Hi, how can I help?',
        source_language: 'en',
        target_language: languageCode,
      })

      setMessages((messages) => [
        {
          ...messages[0],
          text: result.translated_text,
        },
        ...messages.slice(1),
      ])

      setSelectedLanguage(languageCode)
    } catch (error) {
      console.error('Translation error:', error)
    }
  }

  return (
    <Box sx={{ height: '100%', flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
      <Box
        sx={{
          backgroundColor: orgSettings.organization.bg_color
            ? `#${orgSettings.organization.bg_color}`
            : undefined,
          padding: '15px',
          color: orgSettings.organization.font_color
            ? `#${orgSettings.organization.font_color}`
            : undefined,
          display: 'flex',
          alignItems: 'center',
          gap: 2,
        }}
      >
        {orgSettings.organization.image && (
          <Avatar
            src={orgSettings.organization.image}
            size='40px'
            alt={orgSettings.organization.name || 'Organization Logo'}
          />
        )}
        <Typography variant='h6'>
          {orgSettings.chat_name || `Chat with ${orgSettings.organization.name}`}
        </Typography>
        <Box sx={{ marginLeft: 'auto' }}>
          <IconButton onClick={() => setLanguageDrawerOpen(true)} aria-label='Supported Languages'>
            <TranslateIcon
              sx={{
                color: orgSettings.organization.font_color
                  ? `#${orgSettings.organization.font_color}`
                  : undefined,
              }}
            />
          </IconButton>
        </Box>
      </Box>

      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          backgroundColor: 'var(--lightoffwhite)',
          gap: 2,
          p: 2,
        }}
        role='log'
        aria-label='Chat messages'
      >
        <LanguagePrompt onShowMore={() => setLanguageDrawerOpen(true)} />
        {messages.map((message, index) => (
          <MessageBubble
            key={message.id}
            message={message}
            orgSettings={orgSettings}
            showDebugInfo={showDebugInfo}
            ref={index === messages.length - 1 ? latestMessageRef : undefined}
            tabIndex={0}
            aria-setsize={messages.length}
            aria-posinset={index + 1}
          />
        ))}
        {isAddressWidgetOpen && (
          <Box sx={{ mt: 2, width: '100%', display: 'flex', justifyContent: 'center' }}>
            <LocationEntryInline
              onSave={(address: ILocationEntryInlineAddress) => handleAddAddress(address)}
              onCancel={handleCancelAddAddress}
              initialPosition={initialLocation || DEFAULT_LOCATION}
            />
          </Box>
        )}
        {isLoading && (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', mb: 2 }}>
            <ChatBubbleProgressIndicator
              loadingState={loadingState}
              onComplete={isResponseComplete}
            />
          </Box>
        )}
        <div ref={messagesEndRef} />
      </Box>

      <Paper
        component='form'
        onSubmit={handleSubmit}
        sx={{
          p: 2,
          pb: 1,
          borderTop: '1px solid #e0e0e0',
        }}
      >
        <Box sx={{ width: '100%', display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            fullWidth
            multiline
            maxRows={4}
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            placeholder='Type your message...'
            variant='outlined'
            disabled={isLoading}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault()
                handleSubmit()
              }
            }}
            inputRef={inputRef}
          />
          <IconButton
            color='primary'
            onClick={() => handleSubmit()}
            disabled={isLoading || !inputValue.trim()}
            aria-label='Send message'
          >
            <SendIcon />
          </IconButton>
        </Box>
        {messages.length > 0 && (
          <Box sx={{ mt: 2, textAlign: 'center' }}>
            <Typography variant='body2' sx={{ color: '#64748b' }}>
              Chat history is stored in accordance with our{' '}
              <Link
                href='https://www.polimorphic.com/privacy-policy'
                target='_blank'
                sx={{ color: 'secondary.dark', textDecoration: 'underline', fontWeight: 'bold' }}
              >
                privacy policy
              </Link>
              {includePolimorphicLink ? <>. By Polimorphic.</> : null}
            </Typography>
          </Box>
        )}
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mt: 1 }}>
          {isSuper && (
            <Box mr={2}>
              <FormControlLabel
                control={
                  <Switch
                    size='small'
                    checked={debugVisible}
                    onChange={(e) => setDebugVisible(e.target.checked)}
                    color='primary'
                  />
                }
                label={
                  <Typography sx={{ fontSize: '12px', fontFamily: 'monospace' }}>
                    Debug Info
                  </Typography>
                }
                sx={{ m: 0 }}
              />
            </Box>
          )}
        </Box>
      </Paper>

      <Drawer anchor='right' open={languageDrawerOpen} onClose={() => setLanguageDrawerOpen(false)}>
        <Box sx={{ width: 300, p: 2 }}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}
          >
            <Typography variant='h6'>Supported Languages</Typography>
            <IconButton
              onClick={() => setLanguageDrawerOpen(false)}
              aria-label='Close supported languages'
            >
              <CloseIcon />
            </IconButton>
          </Box>
          <Typography variant='body1' sx={{ mb: 2 }}>
            Polimorphic supports a wide range of languages. Simply ask in your language and we'll
            translate the response.
          </Typography>
          <Typography variant='body1' sx={{ mb: 2 }}>
            Here are the languages we support:
          </Typography>
          <List>
            {SUPPORTED_LANGUAGES.map((lang) => (
              <ListItem
                key={lang.code}
                button
                onClick={() => {
                  translateGreeting(lang.code)
                  setLanguageDrawerOpen(false)
                }}
                selected={selectedLanguage === lang.code}
              >
                <ListItemText primary={lang.english} secondary={lang.native} />
              </ListItem>
            ))}
          </List>
        </Box>
      </Drawer>
    </Box>
  )
}

export default ChatView
